import { getLabel } from '@/features/schemas/services/labeller';

// Column width calculator for virtual tables
export const PADDING = 24; // Total horizontal padding for a cell
export const SORT_ICON = 25; // Width for sort icon
export const MIN_WIDTH = 20; // Minimum width for any column
export const ACTIONS_WIDTH = 55; // Fixed width for actions column

// Calculates ideal widths for all columns based on their content
export function calculateIdealWidths(headers, items, measureText, totals) {
    const widths = {};

    headers.forEach(header => {
        widths[header.value] = getIdealColumnWidth(header, items, measureText, totals);
    });

    return widths;
}

// Gets the ideal width for a single column
function getIdealColumnWidth(header, items, measureText, totals) {
    if (header.value === 'actions') {
        return ACTIONS_WIDTH;
    }

    const headerWidth = getHeaderWidth(header, measureText);
    const contentWidth = getMaxContentWidth(header, items, measureText, totals);

    return Math.max(MIN_WIDTH, headerWidth, contentWidth);
}

// Calculates the width needed for the header text and sort icon
function getHeaderWidth(header, measureText) {
    const textWidth = measureText(header.text);
    const sortIconSpace = header.sortable !== false ? SORT_ICON : 0;
    return textWidth + PADDING + sortIconSpace;
}

// Finds the maximum content width across all rows for this column
function getMaxContentWidth(header, items, measureText, totals) {
    let maxWidth = 0;

    // Check all data items
    // items might be a Map directly or a ref-like object with .value containing the Map
    let itemsMap;
    if (items instanceof Map) {
        itemsMap = items;
    } else if (items?.value instanceof Map) {
        itemsMap = items.value;
    } else {
        return maxWidth;
    }

    itemsMap.forEach(item => {
        const width = getItemWidth(header, item, measureText);
        if (width > maxWidth) {
            maxWidth = width;
        }
    });

    // Also check the totals row if it exists
    // totals might be an object directly or a ref-like object with .value
    const totalsValue = totals?.value ?? totals;
    if (totalsValue && header.value in totalsValue) {
        const totalsWidth = getItemWidth(header, totalsValue, measureText);
        if (totalsWidth > maxWidth) {
            maxWidth = totalsWidth;
        }
    }

    return maxWidth;
}

// Calculates the width needed for a specific cell's content
function getItemWidth(header, item, measureText) {
    if (!(header.value in item)) {
        return 0;
    }

    let value = item[header.value];

    // For foreign key fields, use the labeller to get the proper display value
    if (header.entityKey && header.value.endsWith('Id')) {
        const relatedFieldName = header.value.slice(0, -2); // Remove 'Id' suffix
        if (relatedFieldName in item && item[relatedFieldName]) {
            const relatedObject = item[relatedFieldName];
            // Use the labeller to get the display value for this entity
            value = getLabel(header.entityKey, relatedObject);
        }
    }

    const formatted = formatCellValue(header, value);
    return measureText(formatted) + PADDING;
}

// Formats a cell value using the header's formatter if available
function formatCellValue(header, value) {
    if (value === null || value === undefined) {
        return '';
    }

    if (header.formatter) {
        const formatted = header.formatter(value);
        if (formatted === null || formatted === undefined) {
            return '';
        }
        return String(formatted);
    }
    return String(value);
}

/**
 * Determines which column should grow to fill available space.
 * If there's an actions column, the column before it should grow.
 * Otherwise, the last column should grow.
 */
export function shouldGrowColumn(index, headers) {
    // Find if there's an actions column
    const actionsColumnIndex = headers.findIndex(header => header.value === 'actions');
    if (actionsColumnIndex === -1) {
        // No actions column, last column should grow
        return index === headers.length - 1;
    }
    // Actions column exists, column before actions should grow
    return index === actionsColumnIndex - 1;
}

export default {
    PADDING,
    SORT_ICON,
    MIN_WIDTH,
    ACTIONS_WIDTH,
    calculateIdealWidths,
    shouldGrowColumn
};
